import { asText } from '@prismicio/helpers';
import { InputController, InputLabel } from '@remote-com/mushu/src/components/Ui/Form/Input';
import { InputsContainer } from '@remote-com/mushu/src/domains/registration/onboarding/shared/styles/Form';
import { Button } from '@remote-com/norma/core/button';
import styled, { css } from 'styled-components';

import { fluidProp, pixelToRem, whenProp } from '../../style/helpers';
import { breakpointFrom, breakpointUntil } from '../../style/mediaQueries';

export const BannerHead = styled.div`
  z-index: 1;
  ${fluidProp(['padding-left', 'padding-right'], '24px', '36px')};
  ${fluidProp('padding-top', '24px', '36px')};
`;

export const BannerTitle = styled.h2`
  && {
    font-family: 'Bossa', var(--typography-fontPrimary);
    font-size: ${pixelToRem('33px')};
    color: var(--colors-blank);
    font-weight: 700;
    margin: 0;
    margin-bottom: 12px;
  }
`;

export const BannerDescription = styled.div`
  font-family: 'Remote Sans', var(--typography-fontPrimary);
  line-height: 1.4;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 400;
  font-size: ${pixelToRem('16px')};

  ${whenProp(
    '$largeText',
    css`
      font-size: ${pixelToRem('18px')};
    `
  )};

  p {
    margin: 0 0 1em;
  }

  & > * {
    font-size: inherit;
    font-family: inherit;
    font-weight: inherit;
    line-height: inherit;
    color: inherit;
  }
`;

export const BannerBody = styled.div`
  ${fluidProp('padding-right', '24px', '36px')};
  ${fluidProp('padding-left', '24px', '36px')};
  display: grid;
  z-index: 1;
  grid-template-columns: 1fr;
  font-size: ${pixelToRem('16px')};

  ${whenProp(
    '$illustration',
    css`
      && {
        padding-right: 0;
      }
      grid-template-columns: 3fr 2fr;
    `
  )};

  ${breakpointUntil.md(css`
    grid-template-columns: 1fr;
  `)}
`;

export const BannerContent = styled.div`
  ${fluidProp(['padding-bottom', 'padding-top'], '24px', '36px')};
  padding-right: 32px;
  z-index: 1;
  max-width: 600px;
  justify-content: space-between;
`;

export const IllustrationWrapper = styled.div`
  width: 100%;
  align-self: end;
  display: flex;
  align-items: flex-end;
  justify-content: center;

  img {
    border-bottom-right-radius: 10px;
  }

  ${breakpointUntil.md(css`
    display: none;
  `)}
`;

export const CTAButton = styled(Button).attrs({
  shadow: true,
})`
  && {
    cursor: pointer;
    padding: 6px 24px;
    color: var(--newBrandColor);
    text-transform: uppercase;
    background-color: var(--colors-blank);
    border-color: var(--colors-blank);
    font-size: ${pixelToRem('13px')};
    font-weight: 400;
    letter-spacing: 0.1em;
    line-height: 1.3;

    &:disabled {
      background-color: var(--colors-spindle);
      cursor: not-allowed;
    }

    &:hover {
      background-color: var(--colors-spindle);
      border-color: var(--colors-spindle);
      text-decoration: none;
    }

    &:focus-visible {
      && {
        box-shadow: 0 0 0 2px var(--colors-irisBlue), 0 0 0 3px #ffffff;
      }
    }
  }
`;

export const FormWrapper = styled.div`
  ${InputsContainer} {
    margin: 0;
  }

  ${InputController} + ${InputController} {
    margin-top: 0px;
    font-size: ${pixelToRem('20px')};
    line-height: 1.2;
  }

  ${InputLabel} {
    color: inherit;

    input {
      font-size: ${pixelToRem('20px')};
      line-height: 1.2;

      &::placeholder {
        color: inherit;
      }
    }
  }
`;

export const SuccessWrapper = styled.div`
  background: var(--colors-blank);
  padding: 32px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const SuccessMessage = styled.h3`
  && {
    text-align: center;
    color: var(--colors-irisBlue);
  }
`;

export function getIdForBanner(field) {
  if (!field) {
    return null;
  }
  let bannerId = field;
  if (Array.isArray(field)) {
    bannerId = asText(field);
  }

  return bannerId.replace(/\W/g, '-').toLowerCase();
}

export const BannerWrapper = styled.div`
  width: 100%;
  ${fluidProp(['margin-top', 'margin-bottom'], '12px', '64px')};
  border-radius: 10px;
  position: relative;
  color: var(--colors-blank);
  background-color: var(--newBrandColor);

  ${({ $backgroundImage }) =>
    $backgroundImage &&
    css`
      background-image: url('${$backgroundImage.url}'),
        linear-gradient(to right, var(--newBrandColor), var(--newBrandColor));
      background-position: bottom right;
      background-size: auto 100%;
      background-repeat: no-repeat;
    `};

  ${whenProp(
    '$reducedSpacing',
    css`
      ${fluidProp(['margin-bottom', 'margin-top'], '0px', '-36px')};
    `
  )};

  ${({ $size }) =>
    $size === 'compact' &&
    css`
      display: none;
      ${breakpointFrom.xl(css`
        && {
          display: block;
          background: var(--colors-linkWater);
          margin: 0;
          border-radius: 0;
          padding: 35px 28px;
          position: absolute;
          right: -240px;
          top: 0;
          width: 210px;
          color: var(--colors-darkBlue);

          ${BannerHead} {
            padding: 0;
            width: 100%;
          }

          ${BannerTitle} {
            font-family: var(--typography-fontPrimary);
            color: inherit;
            font-size: ${pixelToRem('24px')};
            font-weight: 500;

          }

          ${BannerBody} {
            padding: 0;
            grid-template-columns: 1fr;
          }

          ${IllustrationWrapper} {
            display: none;
          }

          ${BannerContent} {
            width: 100%;
            padding: 0;
          }

          ${BannerDescription} {
            font-family: var(--typography-fontPrimary);
            color: inherit;
          }

          ${CTAButton} {
            font-family: var(--typography-fontPrimary);
            font-size: 14px;
            font-weight: 500;
            letter-spacing: normal
            margin-top: 28px;
            text-transform: capitalize;
            color: var(--colors-blank);
            background-color: var(--colors-irisBlue);
            border-color: var(--colors-irisBlue);

            &:hover {
              background-color: var(--colors-spindle);
              border-color: var(--colors-spindle);
            }
          }

          ${FormWrapper} {
            color: inherit;
            margin: 0;
            padding: 0;

            ${InputLabel} {
              input {
                font-size: ${pixelToRem('16px')};
                color: var(--colors-darkBlue);
                border-color: var(--colors-geyser);
                border-size: 1px;

                &::placeholder {
                  color: var(--colors-lynch);
                }
              }
            }
          }

          ${SuccessWrapper} {
            padding: 0px;
            background: transparent;
          }
         
        }
      `)}
    `};
`;
export const BannerPosition = styled.div`
  width: 100%;
  position: relative;

  ${({ $sticky }) =>
    $sticky === 'compact' &&
    css`
      ${breakpointFrom.xl(css`
        && {
          position: sticky;
          top: 90px;
          height: ${({ $bannerHeight = '500px' }) => $bannerHeight}px;
          margin-top: ${({ $bannerHeight = '-500px' }) => -$bannerHeight}px;
        }

        ${whenProp(
          '$marketingBannerFirstBlock',
          css`
            && {
              position: absolute;
              right: -50px;
              width: 0;
              height: 100%;
              margin-top: 0px;

              ${BannerWrapper} {
                position: sticky;
                top: 90px;
              }
            }
          `
        )}
      `)}
    `}
`;
